import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import { apiRequest, getAuthToken } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { ListSkeleton } from "@/components/skeletons";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  AlertTriangle,
  CheckCircle,
  ChevronDown,
  ChevronRight,
  ClipboardList,
  Loader2,
  RefreshCw,
  Search,
} from "lucide-react";

/**
 * VP-330: swap the Instant Form on ads that are already running.
 *
 * Flow: pick a campaign or ad set → preview which ads carry a form → pick the
 * replacement form (loaded from the page those ads post from) → run the swap
 * with live per-ad progress.
 */

type SwapScope = "campaign" | "adset";

type AdSetEntity = {
  adSetId: string;
  adSetName: string;
  campaignId: string;
  status: string;
};

type CampaignEntity = {
  campaignId: string;
  campaignName: string;
  status: string;
  adSets: AdSetEntity[];
};

type SwapSkipReason =
  | "no_lead_form"
  | "already_using_form"
  | "no_creative_spec"
  | "dynamic_creative"
  | "other_page";

type PreviewAd = {
  adId: string;
  adName: string;
  status: string;
  currentFormId: string | null;
  currentFormIds: string[];
  pageId: string | null;
  eligible: boolean;
  skipReason?: SwapSkipReason;
};

type PreviewResponse = {
  scope: string;
  targetId: string;
  newFormId: string | null;
  newFormName: string | null;
  ads: PreviewAd[];
  eligibleCount: number;
  skippedCount: number;
  pageIds: string[];
};

type SwapAdResult = {
  adId: string;
  adName: string;
  status: "swapped" | "skipped" | "failed";
  previousFormId: string | null;
  newCreativeId?: string;
  error?: string;
};

type SwapResponse = {
  success?: boolean;
  message?: string;
  total?: number;
  swapped?: number;
  skipped?: number;
  failed?: number;
  results?: SwapAdResult[];
};

type LeadFormSwapPanelProps = {
  forUserId?: string;
  clientAdAccountIds?: string[];
};

const SELECT_TRIGGER_CLS =
  "bg-white/[0.03] border-white/[0.08] text-white text-xs h-9 hover:bg-white/[0.05]";
const SELECT_CONTENT_CLS = "bg-[#0a0c10] border-white/[0.1] text-white";
const SELECT_ITEM_CLS = "text-xs text-white/80 focus:bg-white/[0.06] focus:text-white";

export default function LeadFormSwapPanel({
  forUserId,
  clientAdAccountIds,
}: LeadFormSwapPanelProps) {
  const { t, i18n } = useTranslation();
  const { toast } = useToast();
  const [visible, setVisible] = useState(false);
  const [scope, setScope] = useState<SwapScope>("adset");
  const [campaignId, setCampaignId] = useState("");
  const [adSetId, setAdSetId] = useState("");
  const [newFormId, setNewFormId] = useState("");
  const [preview, setPreview] = useState<PreviewResponse | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [swapping, setSwapping] = useState(false);
  const [progress, setProgress] = useState<{ done: number; total: number } | null>(null);
  const [result, setResult] = useState<SwapResponse | null>(null);

  const entitiesQuery = useQuery<{ campaigns: CampaignEntity[] }>({
    queryKey: ["analyst-entities", forUserId, clientAdAccountIds],
    queryFn: () =>
      apiRequest("POST", "/api/marketing-analyst/entities", { forUserId, clientAdAccountIds }),
    enabled: visible,
    staleTime: 60_000,
  });

  const campaigns = entitiesQuery.data?.campaigns || [];
  const selectedCampaign = campaigns.find((c) => c.campaignId === campaignId);
  const targetId = scope === "campaign" ? campaignId : adSetId;

  // The form list belongs to the page those ads post from — known only after preview.
  const previewPageId = preview?.pageIds?.[0] || "";
  const formsQuery = useQuery<{ forms: { id: string; name: string; status: string }[] }>({
    // forUserId is part of the key: an agency switching clients must not reuse the
    // previous client's form list.
    queryKey: ["campaign-creator-leadgen-forms", previewPageId, forUserId],
    queryFn: () =>
      apiRequest(
        "GET",
        `/api/campaign-creator/leadgen-forms?pageId=${encodeURIComponent(previewPageId)}` +
          (forUserId ? `&forUserId=${encodeURIComponent(forUserId)}` : "")
      ),
    enabled: visible && !!previewPageId,
    staleTime: 300_000,
  });
  const forms = formsQuery.data?.forms || [];

  // Distinct forms currently in use — drives the "these ads mix forms" warning.
  const currentFormIds = useMemo(
    () => [...new Set((preview?.ads || []).flatMap((a) => a.currentFormIds || []))],
    [preview]
  );

  const resetRun = () => {
    setPreview(null);
    setResult(null);
    setProgress(null);
    setNewFormId("");
  };

  const runPreview = async (opts: { formId?: string; keepResult?: boolean } = {}) => {
    if (!targetId) return;
    setPreviewLoading(true);
    if (!opts.keepResult) setResult(null);
    try {
      const data = (await apiRequest("POST", "/api/campaign-creator/lead-form-swap/preview", {
        scope,
        id: targetId,
        // With a form chosen the plan also reports ads that already use it.
        ...(opts.formId ? { newFormId: opts.formId } : {}),
        forUserId,
      })) as PreviewResponse;
      setPreview(data);
      if (data.ads.length === 0) {
        toast({ title: t("leadFormSwap.noAdsFound"), variant: "destructive" });
      }
    } catch (err) {
      toast({
        title: t("leadFormSwap.previewFailed"),
        description: err instanceof Error ? err.message : undefined,
        variant: "destructive",
      });
    } finally {
      setPreviewLoading(false);
    }
  };

  /** SSE stream: per-ad progress, then one terminal `done` event with the summary. */
  const runSwap = async () => {
    if (!targetId || !newFormId) return;
    setSwapping(true);
    setResult(null);
    setProgress(null);
    try {
      const token = getAuthToken();
      const res = await fetch("/api/campaign-creator/lead-form-swap", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-Locale": i18n.language,
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        credentials: "include",
        body: JSON.stringify({ scope, id: targetId, newFormId, forUserId }),
      });

      if (!res.headers.get("content-type")?.includes("text/event-stream")) {
        const body = await res.json().catch(() => ({}));
        throw new Error((body as { message?: string }).message || t("leadFormSwap.swapFailed"));
      }

      const reader = res.body?.getReader();
      if (!reader) throw new Error(t("leadFormSwap.swapFailed"));
      const decoder = new TextDecoder();
      let buffer = "";
      let final: SwapResponse | null = null;

      for (;;) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const chunks = buffer.split("\n\n");
        buffer = chunks.pop() || "";
        for (const chunk of chunks) {
          // Heartbeats arrive as SSE comments (": heartbeat") and carry no data line.
          const line = chunk.split("\n").find((l) => l.startsWith("data: "));
          if (!line) continue;
          let event: Record<string, unknown>;
          try {
            event = JSON.parse(line.slice(6)) as Record<string, unknown>;
          } catch {
            continue; // one malformed frame must not kill the whole run
          }
          if (event.type === "step") {
            setProgress({ done: Number(event.done) || 0, total: Number(event.total) || 0 });
          } else if (event.type === "done") {
            final = event as SwapResponse;
          }
        }
      }

      if (!final) throw new Error(t("leadFormSwap.swapFailed"));
      setResult(final);
      if (final.success) {
        toast({ title: t("leadFormSwap.swapDone", { count: final.swapped ?? 0 }) });
        await runPreview({ formId: newFormId, keepResult: true });
      } else {
        toast({
          title: t("leadFormSwap.swapPartial"),
          description: final.message,
          variant: "destructive",
        });
      }
    } catch (err) {
      toast({
        title: t("leadFormSwap.swapFailed"),
        description: err instanceof Error ? err.message : undefined,
        variant: "destructive",
      });
    } finally {
      setSwapping(false);
      setProgress(null);
    }
  };

  return (
    <div className="space-y-3">
      <button
        onClick={() => setVisible(!visible)}
        className={`w-full group flex items-center justify-between rounded-xl border transition-all duration-200 px-4 py-4 ${
          visible
            ? "border-white/[0.12] bg-white/[0.04]"
            : "border-white/[0.08] bg-white/[0.02] hover:bg-white/[0.04] hover:border-white/[0.12]"
        }`}
      >
        <div className="flex items-center gap-3">
          <div className="flex items-center justify-center w-10 h-10 rounded-xl bg-white/[0.06] border border-white/[0.08]">
            <ClipboardList className="w-5 h-5 text-white/60" />
          </div>
          <div className="text-left">
            <span className="text-sm font-semibold text-white block">
              {t("leadFormSwap.title")}
            </span>
            <span className="text-xs text-white/40">{t("leadFormSwap.subtitle")}</span>
          </div>
        </div>
        {visible ? (
          <ChevronDown className="w-4 h-4 text-white/40" />
        ) : (
          <ChevronRight className="w-4 h-4 text-white/30" />
        )}
      </button>

      {visible && (
        <div className="bg-white/[0.02] border border-white/[0.08] rounded-xl overflow-hidden">
          <div className="flex items-center justify-between px-4 py-2 border-b border-white/[0.06]">
            <span className="text-xs font-semibold text-white/70">
              {t("leadFormSwap.pickTarget")}
            </span>
            <button
              onClick={() => entitiesQuery.refetch()}
              disabled={entitiesQuery.isLoading}
              className="flex items-center gap-1 px-2 py-1 rounded text-[10px] text-white/50 hover:text-white/70 hover:bg-white/[0.04] transition-colors"
            >
              <RefreshCw className={`w-3 h-3 ${entitiesQuery.isLoading ? "animate-spin" : ""}`} />
              {t("leadFormSwap.refresh")}
            </button>
          </div>

          <div className="p-3 space-y-3">
            {entitiesQuery.isLoading && <ListSkeleton rows={3} withAvatar={false} />}

            {entitiesQuery.isError && (
              <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-red-950/30 border border-red-700/30 text-red-300 text-xs">
                <AlertTriangle className="w-3.5 h-3.5 flex-shrink-0" />
                <span>
                  {(entitiesQuery.error as Error)?.message || t("leadFormSwap.loadFailed")}
                </span>
              </div>
            )}

            {!entitiesQuery.isLoading && !entitiesQuery.isError && (
              <>
                {/* Scope */}
                <div className="flex gap-1 p-1 rounded-lg bg-white/[0.03] border border-white/[0.06]">
                  {(["adset", "campaign"] as SwapScope[]).map((value) => (
                    <button
                      key={value}
                      type="button"
                      onClick={() => {
                        setScope(value);
                        resetRun();
                      }}
                      className={`flex-1 text-[11px] py-1.5 px-2 rounded-md transition-colors ${
                        scope === value
                          ? "bg-emerald-600/20 text-emerald-300 border border-emerald-500/30"
                          : "text-white/40 hover:text-white/60"
                      }`}
                    >
                      {t(`leadFormSwap.scope.${value}`)}
                    </button>
                  ))}
                </div>

                {/* Campaign */}
                <div className="space-y-1.5">
                  <span className="text-[11px] text-white/50">{t("leadFormSwap.campaign")}</span>
                  <Select
                    value={campaignId}
                    onValueChange={(value) => {
                      setCampaignId(value);
                      setAdSetId("");
                      resetRun();
                    }}
                  >
                    <SelectTrigger className={SELECT_TRIGGER_CLS}>
                      <SelectValue placeholder={t("leadFormSwap.campaignPlaceholder")} />
                    </SelectTrigger>
                    <SelectContent className={SELECT_CONTENT_CLS}>
                      {campaigns.map((c) => (
                        <SelectItem
                          key={c.campaignId}
                          value={c.campaignId}
                          className={SELECT_ITEM_CLS}
                        >
                          {c.campaignName}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Ad set */}
                {scope === "adset" && (
                  <div className="space-y-1.5">
                    <span className="text-[11px] text-white/50">{t("leadFormSwap.adSet")}</span>
                    <Select
                      value={adSetId}
                      onValueChange={(value) => {
                        setAdSetId(value);
                        resetRun();
                      }}
                      disabled={!selectedCampaign}
                    >
                      <SelectTrigger className={SELECT_TRIGGER_CLS}>
                        <SelectValue placeholder={t("leadFormSwap.adSetPlaceholder")} />
                      </SelectTrigger>
                      <SelectContent className={SELECT_CONTENT_CLS}>
                        {(selectedCampaign?.adSets || []).map((a) => (
                          <SelectItem key={a.adSetId} value={a.adSetId} className={SELECT_ITEM_CLS}>
                            {a.adSetName}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}

                <button
                  type="button"
                  onClick={() => void runPreview({ formId: newFormId || undefined })}
                  disabled={!targetId || previewLoading}
                  className="w-full flex items-center justify-center gap-2 py-2 rounded-lg border border-white/[0.08] bg-white/[0.03] text-xs text-white/80 hover:bg-white/[0.06] disabled:opacity-40 transition-colors"
                >
                  {previewLoading ? (
                    <Loader2 className="w-3.5 h-3.5 animate-spin" />
                  ) : (
                    <Search className="w-3.5 h-3.5" />
                  )}
                  {t("leadFormSwap.checkAds")}
                </button>

                {/* Preview */}
                {preview && (
                  <div className="space-y-2 pt-1">
                    <div className="flex items-center justify-between text-[11px]">
                      <span className="text-white/60">
                        {t("leadFormSwap.previewSummary", {
                          eligible: preview.eligibleCount,
                          total: preview.ads.length,
                        })}
                      </span>
                      {preview.skippedCount > 0 && (
                        <span className="text-amber-400/70">
                          {t("leadFormSwap.skippedCount", { count: preview.skippedCount })}
                        </span>
                      )}
                    </div>

                    <div className="max-h-52 overflow-y-auto custom-scrollbar space-y-1">
                      {preview.ads.map((ad) => (
                        <div
                          key={ad.adId}
                          className="flex items-center justify-between gap-2 px-2.5 py-1.5 rounded-lg bg-white/[0.02] border border-white/[0.06]"
                        >
                          <span className="text-[11px] text-white/70 truncate flex-1">
                            {ad.adName}
                          </span>
                          <span className="text-[10px] text-white/35 flex-shrink-0">
                            {ad.eligible
                              ? t("leadFormSwap.currentForm", { id: ad.currentFormId })
                              : t(`leadFormSwap.skipReason.${ad.skipReason || "no_lead_form"}`)}
                          </span>
                        </div>
                      ))}
                    </div>

                    {currentFormIds.length > 1 && (
                      <p className="text-[10px] text-amber-400/70">
                        {t("leadFormSwap.mixedFormsWarning", { count: currentFormIds.length })}
                      </p>
                    )}
                    {preview.pageIds.length > 1 && (
                      <p className="text-[10px] text-amber-400/70">
                        {t("leadFormSwap.mixedPagesWarning")}
                      </p>
                    )}

                    {/* New form */}
                    <div className="space-y-1.5 pt-1">
                      <span className="text-[11px] text-white/50">{t("leadFormSwap.newForm")}</span>
                      {formsQuery.isLoading ? (
                        <ListSkeleton rows={1} withAvatar={false} />
                      ) : forms.length > 0 ? (
                        <Select
                          value={newFormId}
                          onValueChange={(value) => {
                            setNewFormId(value);
                            void runPreview({ formId: value });
                          }}
                        >
                          <SelectTrigger className={SELECT_TRIGGER_CLS}>
                            <SelectValue placeholder={t("leadFormSwap.newFormPlaceholder")} />
                          </SelectTrigger>
                          <SelectContent className={SELECT_CONTENT_CLS}>
                            {forms.map((f) => (
                              <SelectItem key={f.id} value={f.id} className={SELECT_ITEM_CLS}>
                                {f.name} {f.status !== "ACTIVE" ? `(${f.status})` : ""}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      ) : (
                        <p className="text-[10px] text-white/40">{t("leadFormSwap.noForms")}</p>
                      )}
                    </div>

                    {/* Meta forces a brand new creative — the old post's social proof is lost. */}
                    <div className="flex items-start gap-2 px-2.5 py-2 rounded-lg bg-amber-950/20 border border-amber-700/30">
                      <AlertTriangle className="w-3.5 h-3.5 text-amber-400/80 flex-shrink-0 mt-0.5" />
                      <span className="text-[10px] text-amber-200/80">
                        {t("leadFormSwap.socialProofWarning")}
                      </span>
                    </div>

                    <button
                      type="button"
                      onClick={runSwap}
                      disabled={swapping || !newFormId || preview.eligibleCount === 0}
                      className="w-full flex items-center justify-center gap-2 py-2 rounded-lg border border-emerald-500/30 bg-emerald-600/20 text-xs text-emerald-200 hover:bg-emerald-600/30 disabled:opacity-40 transition-colors"
                    >
                      {swapping ? (
                        <Loader2 className="w-3.5 h-3.5 animate-spin" />
                      ) : (
                        <ClipboardList className="w-3.5 h-3.5" />
                      )}
                      {swapping && progress
                        ? t("leadFormSwap.swapProgress", {
                            done: progress.done,
                            total: progress.total,
                          })
                        : t("leadFormSwap.swapButton", { count: preview.eligibleCount })}
                    </button>
                  </div>
                )}

                {/* Result */}
                {result && (
                  <div className="space-y-1.5">
                    <div
                      className={`flex items-center gap-2 px-3 py-2 rounded-lg border text-xs ${
                        result.success
                          ? "bg-emerald-950/30 border-emerald-700/30 text-emerald-300"
                          : "bg-red-950/30 border-red-700/30 text-red-300"
                      }`}
                    >
                      {result.success ? (
                        <CheckCircle className="w-3.5 h-3.5 flex-shrink-0" />
                      ) : (
                        <AlertTriangle className="w-3.5 h-3.5 flex-shrink-0" />
                      )}
                      <span>{result.message}</span>
                    </div>
                    {(result.results || [])
                      .filter((r) => r.status === "failed")
                      .map((r) => (
                        <div key={r.adId} className="text-[10px] text-red-300/70 px-3">
                          {r.adName}: {r.error}
                        </div>
                      ))}
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
